import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, ListRowProps } from 'react-virtualized';
import Selection, { SelectionEvent } from '@simonwep/selection-js';
import moment, { Moment } from 'moment';
import { v4 as uuid } from 'uuid';

import { Property, PropertyGrouped, PropertyGroupedClosed } from '@/services/Property/types';
import { Reservation } from '@/services/Reservation/types';

import { useCheckBox } from '@/context/CheckBoxContext';
import { useUpdateReservation } from '@/context/UpdateReservationContext';
import { useUser } from '@/context/UserContext';

import { useReservationForm } from '@/hooks/ReservationHook/useReservationForm';

import ReservationMarker from '@/components/ReservationMarker';

import {
  CalendarContainerCol,
  CalendarContainerRow,
  CalendarGroup,
  ContainerGroup,
  ContainerProperty,
  Price,
} from './styles';

import 'react-virtualized/styles.css';

type Props = {
  className?: string;
  groupedData: PropertyGrouped[];
  propertyGroupedClosed: PropertyGroupedClosed;
  dates: Moment[];
  onClick: Function;
};

type SelectEl = {
  cols: number;
  rows: number;
  groups: number;
  id: string;
  indexDate: string;
  price: string;
  guaranteeValue: string;
  idGroup: string | number;
  properties: string[];
};

const PropertiesDates: FC<Props> = ({ groupedData, propertyGroupedClosed, dates, onClick }) => {
  const { userInformation } = useUser();
  const { resetSelection, setData } = useReservationForm();
  const { updateReservation } = useUpdateReservation();
  const { isShowingPrice } = useCheckBox();
  const [groupSelect, setGroupSelect] = useState<SelectEl | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const roles = userInformation?.roles || [];

  useEffect(() => {
    setTimeout(() => {
      setLoading(true);
    }, 500);
  }, []);

  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 1,
    }),
  );

  const handleSetData = useCallback(() => {
    setData({
      price: Number(groupSelect?.price),
      guaranteeValue: Number(groupSelect?.guaranteeValue),
      start: dates[Number(groupSelect?.indexDate)],
      end: moment(dates[Number(groupSelect?.indexDate)]).add((groupSelect?.cols || 0) - 1, 'days'),
      properties: groupedData.reduce<Property[]>(
        (acc, item) => [
          ...acc,
          ...item.data.filter(
            (property) =>
              groupSelect?.properties && groupSelect?.properties?.includes(property.id.toString()),
          ),
        ],
        [],
      ),
    });
  }, [groupSelect]);

  useEffect(() => {
    handleSetData();
  }, [handleSetData]);

  const handleEventClick = useCallback(() => {
    const selection = new Selection({
      class: 'selection',
      selectables: ['.selection .item'],
      boundaries: ['.selection'],
      scrollSpeedDivider: 20,
      manualScrollSpeed: 750,
    });

    selection
      .on('start', ({ inst, selected }): void => {
        selected.forEach((element) => {
          element?.classList.remove('selected');
          inst.removeFromSelection(element);
        });
        inst.clearSelection();
        const elementsSelected = document.querySelectorAll('.selected');
        elementsSelected.forEach((element) => {
          element?.classList.remove('selected');
        });
      })
      .on('move', ({ changed: { added, removed } }: SelectionEvent) => {
        added.forEach((element) => {
          element?.classList.add('selected');
        });

        if (removed?.forEach) {
          removed.forEach((element) => {
            element?.classList.remove('selected');
          });
        }

        const elSelectedAux = Array.from(document.getElementsByClassName('selected'));
        const elSelected: Element[] = [];

        elSelectedAux.forEach((element) => {
          const codeProperty = element.attributes.getNamedItem('data-grouped-id')?.value || '';
          if (!(codeProperty in propertyGroupedClosed && propertyGroupedClosed[codeProperty])) {
            elSelected.push(element);
          }
        });

        if (elSelected.length === 0) {
          setGroupSelect(null);
          return;
        }

        const calcElements = elSelected.reduce(
          (acc, el: Element) => ({
            rows: {
              ...acc.rows,
              [el.getAttribute('data-property-id') || '']: true,
            },
            cols: {
              ...acc.cols,
              [el.getAttribute('data-date-id') || '']: true,
            },
            groups: {
              ...acc.groups,
              [el.getAttribute('data-grouped-id') || '']: true,
            },
            price: acc.price + Number(el.getAttribute('data-property-price')),
            guaranteeValue: Number(el.getAttribute('data-property-bond')),
          }),
          {
            rows: {},
            cols: {},
            groups: {},
            price: 0,
            guaranteeValue: 0,
          },
        );

        setGroupSelect({
          price: calcElements.price.toFixed(2),
          guaranteeValue: calcElements.guaranteeValue.toFixed(2),
          rows: Object.values(calcElements.rows).length,
          cols: Object.values(calcElements.cols).length,
          groups: Object.values(calcElements.groups).length - 1,
          id: elSelected?.[0]?.getAttribute('data-property-id') || '',
          idGroup: elSelected?.[0]?.getAttribute('data-grouped-id') || '',
          properties: Object.values(
            elSelected.reduce(
              (acc, item: Element) => ({
                ...acc,
                [item.getAttribute('data-property-id') || '']:
                  item.getAttribute('data-property-id'),
              }),
              {},
            ),
          ),
          indexDate: elSelected?.[0]?.getAttribute('data-date-id') || '',
        });
      })
      .on('stop', ({ inst }) => {
        inst.keepSelection();
        onClick();
      });
  }, [onClick, propertyGroupedClosed]);

  useEffect(() => {
    handleEventClick();
  }, [handleEventClick]);

  useEffect(() => {
    if (!groupSelect) {
      const elementsSelected = document.querySelectorAll('.selected');
      elementsSelected.forEach((element) => {
        element?.classList.remove('selected');
      });
    }
  }, [groupSelect]);

  useEffect(() => {
    setGroupSelect(null);
  }, [resetSelection]);

  type InclusivityOptions = '()' | '[)' | '(]' | '[]';
  const checkIfCurrentDateIsBeetweenCheckinAndCheckoutDates = (
    currentDate: Moment,
    date1: Moment,
    date2: Moment,
    inclusivity: InclusivityOptions = '[]',
  ) =>
    moment(currentDate, 'YYYY-MM-DD').isBetween(
      moment(date1, 'YYYY-MM-DD'),
      moment(date2, 'YYYY-MM-DD'),
      'days',
      inclusivity,
    );

  const disableSelect = (reservations: Record<string, Reservation>, date: Moment) => {
    if (reservations) {
      const filterReservations = Object.values(reservations).filter(
        (reservation: Reservation) =>
          checkIfCurrentDateIsBeetweenCheckinAndCheckoutDates(
            moment(date, 'YYYY-MM-DD'),
            moment(reservation.check_in_date, 'YYYY-MM-DD'),
            moment(reservation.check_out_date, 'YYYY-MM-DD'),
            '[]',
          ) && moment(date).format('YYYY-MM-DD') === reservation.check_in_date,
      );
      return filterReservations.length > 0;
    }

    return false;
  };

  const checkIfIsSameDate = (date1: Moment, date2: Moment) => {
    const test = moment(date1.format('YYYY-MM-DD'), 'YYYY-MM-DD').isSame(
      moment(date2.format('YYYY-MM-DD'), 'YYYY-MM-DD'),
    );
    return test;
  };

  const getCheckinsDates = () => {
    let checkinsKeys: Record<string, Array<string>> = {};
    groupedData.forEach((category) =>
      category.data.forEach((property: Property) => {
        if (property?.reservation) {
          checkinsKeys = {
            ...checkinsKeys,
            [property.code]: Object.keys(property.reservation),
          };
        }
      }),
    );
    return checkinsKeys;
  };

  const checkinsDates: Record<string, Array<string>> = getCheckinsDates();

  const calculateSizeReservation = (reservation: Record<string, Reservation>, date: Moment) => {
    let size: number = 0;
    size =
      moment(reservation[date.format('YYYY-MM-DD')]?.check_out_date, 'YYYY-MM-DD').diff(
        moment(reservation[date.format('YYYY-MM-DD')]?.check_in_date, 'YYYY-MM-DD'),
        'day',
      ) + 1;

    const checkinIsBefore = moment(
      reservation[date.format('YYYY-MM-DD')]?.check_in_date,
      'YYYY-MM-DD',
    ).isBefore(moment(dates[0], 'YYYY-MM-DD'));

    size = checkinIsBefore
      ? moment(reservation[date.format('YYYY-MM-DD')]?.check_out_date, 'YYYY-MM-DD').diff(
          moment(dates[0], 'YYYY-MM-DD'),
          'day',
        ) + 1
      : size;

    return Math.abs(size);
  };

  const [widthWindow, setWidthWindow] = useState<number>(window.innerWidth);
  const [widthMarker, setWidthMarker] = useState<number>(0);

  useEffect(() => {
    const handleResize = () => {
      setWidthWindow(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refDiv = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    setWidthMarker(refDiv.current ? refDiv.current.offsetWidth : 0);
  }, [refDiv.current, widthWindow]);

  const rowRender = useCallback(
    ({ index, key, parent }: ListRowProps) => {
      const groupedRow = groupedData[index];
      return (
        <CellMeasurer
          key={key}
          cache={cache.current}
          parent={parent}
          columnIndex={0}
          rowIndex={index}
        >
          <ContainerGroup
            expanded={!propertyGroupedClosed[groupedRow.id]}
            style={{ display: 'block' }}
          >
            <CalendarGroup />
            <ContainerProperty>
              {groupedRow.data.map((property: Property, indexProp: number) => (
                <CalendarContainerRow
                  key={`date-property-${key}-${property.code}-${indexProp.toString()}`}
                >
                  <>
                    {dates.map((date: Moment, indexDate: number) => (
                      <CalendarContainerCol
                        key={`${property.code}-${key}-property-date-${date.format('YYYY-MM-DD')}-${indexProp.toString()}`}
                        ref={refDiv}
                        id={date.format('YYYY-MM-DD')}
                        data-cy={`${property.code}_${date.format('YYYY-MM-DD')}`}
                        data-property-id={property.id}
                        data-property-price={100}
                        data-property-bond={property.bond_amount}
                        data-date-id={indexDate}
                        data-grouped-id={groupedRow.id}
                        borderActive={moment().isSame(date, 'day')}
                        weekend={!!/sábado|sexta/.test(date.format('dddd').split('-')[0])}
                        className={
                          property.reservation && disableSelect(property.reservation, date)
                            ? ''
                            : 'item item-property-group'
                        }
                      >
                        {checkinsDates[property.code]?.length > 0 &&
                          checkinsDates[property.code].map(
                            (checkinDate: string) =>
                              checkIfCurrentDateIsBeetweenCheckinAndCheckoutDates(
                                moment(date, 'YYYY-MM-DD'),
                                moment(checkinDate, 'YYYY-MM-DD'),
                                moment(
                                  property.reservation &&
                                    property.reservation[checkinDate]?.check_out_date,
                                  'YYYY-MM-DD',
                                ),
                              ) &&
                              property.reservation &&
                              property.reservation[checkinDate] && (
                                <ReservationMarker
                                  key={`${checkinDate}-${uuid()}`}
                                  ota={property.reservation[checkinDate].listing.ota}
                                  blocked={property.reservation[checkinDate].is_blocking}
                                  reason={property.reservation[checkinDate].blocking_reason}
                                  paymentStatus={property.reservation[checkinDate].paymentStatus}
                                  reservationStatus={property.reservation[checkinDate].status}
                                  size={calculateSizeReservation(property.reservation, date)}
                                  defaultSize={
                                    indexDate === dates.length - 1 &&
                                    !checkIfIsSameDate(
                                      date,
                                      moment(
                                        property.reservation[checkinDate]?.check_out_date,
                                        'YYYY-MM-DD',
                                      ),
                                    )
                                      ? 3
                                      : 2
                                  }
                                  widthMarker={widthMarker}
                                  id={property.id.toString()}
                                  propertyAddressId={property.address}
                                  client={`
                              ${
                                property.reservation[checkinDate].guest.user.first_name !== 'Dummy'
                                  ? property.reservation[checkinDate].guest.user.first_name
                                  : 'Bloqueio'
                              }
                              ${
                                property.reservation[checkinDate].guest.user.last_name !== 'Dummy'
                                  ? property.reservation[checkinDate].guest.user.last_name
                                  : property.reservation[checkinDate].blocking_reason
                              }
                              `}
                                  price={property.reservation[checkinDate].daily_net_value || 0}
                                  adultGuests={
                                    property.reservation[checkinDate].adult_guest_quantity || 1
                                  }
                                  childrenGuests={
                                    property.reservation[checkinDate].child_guest_quantity || 0
                                  }
                                  petIndicator={property.reservation[checkinDate].has_pet > 0}
                                  emailIndicator={false}
                                  reservationData={property.reservation[checkinDate]}
                                  bookings={Object.keys(property.reservation).map(
                                    (k) => property.reservation && property.reservation[k],
                                  )}
                                  isInitMarker={checkIfIsSameDate(
                                    date,
                                    moment(checkinDate, 'YYYY-MM-DD'),
                                  )}
                                  isEndMarker={checkIfIsSameDate(
                                    date,
                                    moment(
                                      property.reservation[checkinDate]?.check_out_date,
                                      'YYYY-MM-DD',
                                    ),
                                  )}
                                  showIndicators={checkIfIsSameDate(
                                    date,
                                    moment(checkinDate, 'YYYY-MM-DD'),
                                  )}
                                  showIconStatus={checkIfIsSameDate(
                                    date,
                                    moment(
                                      property.reservation[checkinDate]?.check_out_date,
                                      'YYYY-MM-DD',
                                    ),
                                  )}
                                  showIconReservation={checkIfIsSameDate(
                                    date,
                                    moment(checkinDate, 'YYYY-MM-DD'),
                                  )}
                                  totalDays={
                                    moment(
                                      property.reservation[checkinDate].check_out_date,
                                      'YYYY-MM-DD',
                                    ).diff(
                                      moment(
                                        property.reservation[checkinDate].check_in_date,
                                        'YYYY-MM-DD',
                                      ),
                                      'day',
                                    ) + 1
                                  }
                                  isShowingMarker={loading}
                                  isBookingOrBlockAutomatic={
                                    property.reservation[checkinDate].is_last_minute ||
                                    property.reservation[checkinDate].is_block_for_pricing
                                  }
                                />
                              ),
                          )}

                        {isShowingPrice && !roles.includes('Host') && <Price>R$ 100,00</Price>}
                      </CalendarContainerCol>
                    ))}
                  </>
                </CalendarContainerRow>
              ))}
            </ContainerProperty>
          </ContainerGroup>
        </CellMeasurer>
      );
    },
    [groupedData, isShowingPrice, updateReservation, propertyGroupedClosed],
  );

  return (
    <>
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            rowCount={groupedData.length}
            rowHeight={cache.current.rowHeight}
            deferredMeasurementCache={cache.current}
            rowRenderer={rowRender}
            width={width}
          />
        )}
      </AutoSizer>
    </>
  );
};

export default memo(PropertiesDates);
